import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePageSize from "@/hooks/usePageSize";
import usePopupStore from "@/hooks/usePopupStore";
import useSearchFilters from "@/hooks/useSearchFilters";
import ActionsBar from "@/UI/components/ActionsBar";
import Table, { type TableColumn } from "@/UI/components/Table";
import TableCell from "@/UI/components/Table/TableCell";
import { FilterSelect } from "@/UI/inputs/FilterSelect";
import FilterRange from "@/UI/inputs/FilterRange";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import OrderDetailsPopup from "@/UI/popups/OrderDetailsPopup";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

interface Order {
    _id: string;
    number: string;
    customer?: {
        _id: string;
        name: string;
        phone: string;
        picture?: string;
    };
    branch?: {
        _id: string;
        name: string | { lang: string; value: string }[];
        address?: string | { lang: string; value: string }[];
    };
    totalPrice: number;
    status: "completed" | "pending" | "rejected" | "cancelled";
    date: string;
    pickupCode?: string;
    source: "customer" | "client" | "cashier";
    items: {
        _id?: string;
        product: string;
        name: string;
        price: number;
        quantity: number;
    }[];
    createdAt: string;
}

export default function OrdersScreen() {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();
    const [page, setPage] = useState(1);
    const { pageSize, setPageSize } = usePageSize();

    const filters = useSearchFilters({
        search: undefined as string | undefined,
        status: [] as string[],
        source: [] as string[],
        branch: [] as string[],
        min_total: undefined as string | undefined,
        max_total: undefined as string | undefined,
        date_from: undefined as string | undefined,
        date_to: undefined as string | undefined,
    });

    // ── Fields fetch ────────────────────────────────────────────────────────────
    const { data: fieldsData } = useFetch("orders/fields");
    const statusOptions: { title: string; key: string }[] = fieldsData?.data?.statuses ?? [];
    const sourceOptions: { title: string; key: string }[] = fieldsData?.data?.sources ?? [];
    const branchOptions: { _id: string; name: string }[] = fieldsData?.data?.branches ?? [];

    // ── Orders fetch ────────────────────────────────────────────────────────────
    const { data, loading, invoke } = useFetch("orders", { query: { page: 1, limit: pageSize, ...filters.committedQuery } });

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        invoke({ query: { page, limit: pageSize, ...filters.committedQuery } });
    }, [page]);

    useEffect(() => {
        const handler = () => {
            setPage(1);
            invoke({ query: { page: 1, limit: pageSize, ...filters.committedQuery } });
        };
        window.addEventListener("orders:refresh", handler);
        return () => window.removeEventListener("orders:refresh", handler);
    }, []);

    const orders: Order[] = data?.data?.orders ?? [];
    const total: number = data?.data?.total ?? 0;

    const handlePageSizeChange = (size: number) => {
        setPage(1);
        setPageSize(size);
        invoke({ query: { page: 1, limit: size, ...filters.committedQuery } });
    };

    // ── Helper Functions ────────────────────────────────────────────────────────
    const getLocalizedValue = (value: string | { lang: string; value: string }[] | undefined): string => {
        if (!value) return "—";
        if (typeof value === "string") return value;
        if (Array.isArray(value)) {
            const match = value.find((v) => v.lang === lang) ?? value[0];
            return match?.value ?? "—";
        }
        return "—";
    };

    const formatDate = (dateString: string): string => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        return date.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const getStatusColor = (status: Order["status"]): string => {
        const colors = {
            completed: "orders-status-completed",
            pending: "orders-status-pending",
            rejected: "orders-status-rejected",
            cancelled: "orders-status-cancelled",
        };
        return colors[status] || "";
    };

    const handleViewOrder = (order: Order) => {
        const name = `order-details-${order._id}`;
        removeComp(name);
        addComp({
            name,
            Component: OrderDetailsPopup,
            details: {
                orderId: order._id,
                onSuccess: () => {
                    invoke({ query: { page, limit: pageSize, ...filters.committedQuery } });
                },
            },
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Columns ─────────────────────────────────────────────────────────────────
    const columns: TableColumn<Order>[] = [
        {
            key: "number",
            header: t.orders.col.number,
            width: "120px",
            render: (row) => <span className="orders-number">#{row.number}</span>,
        },
        {
            key: "customer",
            header: t.orders.col.customer,
            width: "180px",
            render: (row) => (
                <div className="orders-customer">
                    {row.customer?.picture ? (
                        <img src={row.customer.picture} alt={row.customer.name} className="orders-customer-picture" />
                    ) : (
                        <div className="orders-customer-placeholder">{row.customer?.name?.charAt(0) ?? "?"}</div>
                    )}
                    <TableCell.Text value={row.customer?.name || "—"} maxChars={20} />
                </div>
            ),
        },
        {
            key: "branch",
            header: t.orders.col.branch,
            width: "150px",
            render: (row) => <TableCell.Text value={getLocalizedValue(row.branch?.name)} maxChars={18} />,
        },
        {
            key: "items",
            header: t.orders.col.items,
            width: "80px",
            render: (row) => <span className="orders-items-count">{row.items?.length ?? 0}</span>,
        },
        {
            key: "totalPrice",
            header: t.orders.col.totalPrice,
            width: "120px",
            render: (row) => (
                <span className="orders-price">
                    {row.totalPrice.toFixed(2)} {t.orders.sar}
                </span>
            ),
        },
        {
            key: "status",
            header: t.orders.col.status,
            width: "120px",
            render: (row) => <span className={`orders-status ${getStatusColor(row.status)}`}>{t.orders.statuses[row.status]}</span>,
        },
        {
            key: "date",
            header: t.orders.col.date,
            width: "160px",
            render: (row) => <TableCell.Text value={formatDate(row.createdAt)} />,
        },
        {
            key: "actions",
            header: t.orders.col.actions,
            width: "60px",
            render: (row) => (
                <TableCell.Actions actions={[{ name: "view", icon: "/icons/eye-opened-icon.svg", handler: () => handleViewOrder(row) }]} />
            ),
        },
    ];

    const filterContent = (
        <>
            <FilterSearchInput
                value={filters.draft.search}
                onChange={(v) => filters.setDraftField("search", v)}
                placeholder={t.filters.orders.search}
            />
            {statusOptions.length > 0 && (
                <FilterSelect
                    label={t.filters.orders.status}
                    options={statusOptions.map((o) => ({ value: o.key, label: o.title }))}
                    value={filters.draft.status as string[]}
                    onChange={(v) => filters.setDraftField("status", v)}
                />
            )}
            {sourceOptions.length > 0 && (
                <FilterSelect
                    label={t.filters.orders.source}
                    options={sourceOptions.map((o) => ({ value: o.key, label: o.title }))}
                    value={filters.draft.source as string[]}
                    onChange={(v) => filters.setDraftField("source", v)}
                />
            )}
            {branchOptions.length > 0 && (
                <FilterSelect
                    label={t.filters.orders.branch}
                    options={branchOptions.map((o) => ({ value: o._id, label: o.name }))}
                    value={filters.draft.branch as string[]}
                    onChange={(v) => filters.setDraftField("branch", v)}
                />
            )}
            <FilterRange
                label={t.filters.orders.totalRange}
                fromValue={filters.draft.min_total}
                toValue={filters.draft.max_total}
                onFromChange={(v) => filters.setDraftField("min_total", v)}
                onToChange={(v) => filters.setDraftField("max_total", v)}
                fromPlaceholder={t.filters.from}
                toPlaceholder={t.filters.to}
            />
            <FilterRange
                label={t.filters.orders.dateRange}
                fromValue={filters.draft.date_from}
                toValue={filters.draft.date_to}
                onFromChange={(v) => filters.setDraftField("date_from", v)}
                onToChange={(v) => filters.setDraftField("date_to", v)}
                type="date"
            />
        </>
    );

    return (
        <div className="orders-screen">
            <ActionsBar
                filterContent={filterContent}
                filterOpen={filters.filterOpen}
                onFilterOpen={filters.openFilter}
                onFilterSave={() =>
                    filters.saveFilter((q) => {
                        setPage(1);
                        invoke({ query: { page: 1, limit: pageSize, ...q } });
                    })
                }
                onFilterClear={() =>
                    filters.clearFilter(() => {
                        setPage(1);
                        invoke({ query: { page: 1, limit: pageSize } });
                    })
                }
                onFilterCancel={filters.cancelFilter}
                hasActiveFilters={filters.hasActiveFilters}
            />
            <div className="orders-screen-content">
                <Table
                    columns={columns}
                    data={orders}
                    loading={loading}
                    rowKey="_id"
                    pagination={{ page, pageSize, total, onPageChange: setPage, onPageSizeChange: handlePageSizeChange }}
                />
            </div>
        </div>
    );
}
